import { useEffect, useRef, useState, useCallback } from 'react'
import WarningModal from './WarningModal'
import SuccessModal from './SuccessModal'
import ExitedModal from './ExitedModal'
import Footer from './Footer'
import AxiosInstance from 'utils/AxiosInstance'
import { useSelector } from 'react-redux'
import logoLight from '../../../pages/Login/images/kiwi-full-logo.svg'
import CameraPreview from './CameraPreview'
import { Modal, ModalBody, Button } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExclamationTriangle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min'
import { useDispatch } from 'react-redux'
import { getApplication } from 'store/Recruitment/actions'

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition

function AIInterviewTest() {
  const history = useHistory()
  const location = useLocation()
  const { user } = useSelector((store) => store.auth)

  const [showSetupPage, setShowSetupPage] = useState(true)
  const [spokenTextHistory, setSpokenTextHistory] = useState([])
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [jobTitle, setJobTitle] = useState('')
  const [step, setStep] = useState('intro')
  const [questions, setQuestions] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1)
  const [timeLeft, setTimeLeft] = useState(null)
  const [conclusion, setConclusion] = useState('')
  const [visibleQuestions, setVisibleQuestions] = useState([])
  const [endOnTabChange, setEndOnTabChange] = useState(false)

  const [canInterviewContinue, setCanInterviewContinue] = useState(false)
  const [showExitWarning, setShowExitWarning] = useState(false)
  const [exitCountdown, setExitCountdown] = useState(10)
  const [permissionError, setPermissionError] = useState('')
  const [isChromeBrowser, setIsChromeBrowser] = useState(false)
  const [isSetupLoading, setIsSetupLoading] = useState(false)
  const [validationChecked, setValidationChecked] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showSuccessScreen, setShowSuccessScreen] = useState(false)
  const [showExitedScreen, setShowExitedScreen] = useState(false)

  const audioRef = useRef(null)
  const timerRef = useRef(null)
  const containerRef = useRef(null)
  const recognitionRef = useRef(null)
  const micWasOnRef = useRef(false)

  const mediaRecorderRef = useRef(null)
  const recordedChunksRef = useRef([])
  const [questionTimestamps, setQuestionTimestamps] = useState([])
  const startTimeRef = useRef(null)

  // Store references to streams for cleanup
  const screenStreamRef = useRef(null)
  const micStreamRef = useRef(null)
  const cameraStreamRef = useRef(null)
  const exitTimerRef = useRef(null)

  // Track media stream monitoring
  const mediaStreamMonitorRef = useRef(null)

  // Refs to track current state for fullscreen handler
  const stateRef = useRef({
    canInterviewContinue: false,
    showSetupPage: true,
    step: 'intro',
    showExitWarning: false,
    isUploading: false,
  })

  // Update refs whenever state changes
  useEffect(() => {
    stateRef.current = {
      canInterviewContinue,
      showSetupPage,
      step,
      showExitWarning,
      isUploading,
    }
  }, [canInterviewContinue, showSetupPage, step, showExitWarning, isUploading])

  const { theme } = useSelector((state) => state.themeConfig)
  const { jobApplication } = useSelector((store) => store.recruitment)
  const dispatch = useDispatch()

  const { search } = useLocation()
  const [params, setParams] = useState({})

  // Parse URL params once and memoize
  useEffect(() => {
    const queryParams = new URLSearchParams(search)
    const parsedParams = Object.fromEntries(queryParams.entries())
    setParams(parsedParams)
  }, [search])

  // Helper functions defined with useCallback
  const speakWithOpenAI = useCallback(async (text, onEndCallback) => {
    try {
      setIsSpeaking(true)

      const response = await fetch('https://api.openai.com/v1/audio/speech', {
        method: 'POST',
        headers: {
          Authorization: `Bearer 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`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'tts-1',
          input: text,
          voice: 'shimmer',
        }),
      })

      const blob = await response.blob()

      const audioUrl = URL.createObjectURL(blob)
      const audio = new Audio(audioUrl)

      audioRef.current = audio

      // Add error handling
      audio.onerror = (e) => {
        setIsSpeaking(false)
        if (typeof onEndCallback === 'function') onEndCallback()
      }

      const playPromise = audio.play()

      if (playPromise !== undefined) {
        playPromise
          .then(() => {})
          .catch((error) => {
            setIsSpeaking(false)
            if (typeof onEndCallback === 'function') onEndCallback()
          })
      }

      audio.onended = () => {
        setIsSpeaking(false)
        URL.revokeObjectURL(audioUrl)
        if (typeof onEndCallback === 'function') onEndCallback()
      }
    } catch (error) {
      setIsSpeaking(false)
      if (typeof onEndCallback === 'function') onEndCallback()
    }
  }, [])
  const stopRecordingAndUpload = useCallback(async () => {
    setIsUploading(true)
    return new Promise(async (resolve) => {
      const recorder = mediaRecorderRef.current
      if (!recorder) {
        setIsUploading(false)
        return resolve()
      }

      recorder.onstop = async () => {
        try {
          const blob = new Blob(recordedChunksRef.current, {
            type: 'video/webm',
          })

          // Convert blob to File object with proper filename
          const webmFile = new File([blob], 'recording.webm', {
            type: 'video/webm',
          })

          const formData = new FormData()
          formData.append('video', webmFile)
          formData.append('interview_id', params.interview_id)
          formData.append('timestamps', JSON.stringify(questionTimestamps))

          // Add file metadata that backend expects
          formData.append('originalname', 'recording.webm')
          formData.append('mimetype', 'video/webm')

          // Get the current questions state at the time of upload
          setQuestions((currentQuestions) => {
            formData.append('answers', JSON.stringify(currentQuestions))
            return currentQuestions // Return the same state (no change)
          })

          const response = await AxiosInstance.post(
            '/recruitment/ai-interview/session',
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
            }
          )

          setIsUploading(false)
          // Show success screen instead of returning to setup
          setShowSuccessScreen(true)
          resolve(response.data)
        } catch (err) {
          setIsUploading(false)
          resolve(null)
        }
      }

      recorder.stop()
    })
  }, [questionTimestamps])

  const stopMediaStreamMonitoring = useCallback(() => {
    if (mediaStreamMonitorRef.current) {
      clearInterval(mediaStreamMonitorRef.current)
      mediaStreamMonitorRef.current = null
    }
  }, [])

  const resetToSetupPage = useCallback(
    (showExited = false) => {
      // Stop media stream monitoring
      stopMediaStreamMonitoring()

      // Stop all recording and streams
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        try {
          mediaRecorderRef.current.stop()
        } catch (e) {}
      }

      // Stop all media tracks
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((track) => track.stop())
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop())
      }
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop())
      }

      // Clear all timers
      clearInterval(timerRef.current)
      if (exitTimerRef.current) {
        clearInterval(exitTimerRef.current)
        exitTimerRef.current = null
      }

      // Show exited screen if interview was interrupted, otherwise go to setup
      if (showExited && canInterviewContinue) {
        setShowExitedScreen(true)
        setShowSetupPage(false)
      } else {
        setShowSetupPage(true)
        setShowExitedScreen(false)
      }

      // Reset all states
      setCanInterviewContinue(false)
      setShowExitWarning(false)
      setStep('intro')
      setCurrentQuestionIndex(-1)
      setTimeLeft(null)
      setVisibleQuestions([])
      setSpokenTextHistory([])
      setQuestions([])
      setPermissionError('')
      setIsSetupLoading(false)
    },
    [stopMediaStreamMonitoring, canInterviewContinue]
  )

  const endInterviewEarly = useCallback(
    (reason) => {
      setStep('ended')
      clearInterval(timerRef.current)
      clearInterval(exitTimerRef.current)
      clearInterval(mediaStreamMonitorRef.current)

      // Update backend to mark interview as exited
      AxiosInstance.put(`/recruitment/interviews/${params.interview_id}`, {
        exited_interview: 1,
      }).then(() => {
        // Dispatch action to update the local state
        dispatch(getApplication(params.application_id))
      })

      speakWithOpenAI(`Interview ended: ${reason}`, () => {
        setTimeout(() => {
          // Set exited screen directly since we've marked it as exited
          setShowExitedScreen(true)
          setShowSetupPage(false)
          setCanInterviewContinue(false)
        }, 2000)
      })
    },
    [speakWithOpenAI, params.interview_id, params.application_id, dispatch]
  )

  // Monitor media streams for interruptions
  const startMediaStreamMonitoring = useCallback(() => {
    if (mediaStreamMonitorRef.current) {
      clearInterval(mediaStreamMonitorRef.current)
    }

    // Add event listeners for immediate detection when tracks end
    const addTrackEndListeners = () => {
      // Camera track end listener
      if (cameraStreamRef.current) {
        const videoTracks = cameraStreamRef.current.getVideoTracks()
        videoTracks.forEach((track) => {
          track.addEventListener('ended', () => {
            console.log('Camera track ended event fired')
            if (
              canInterviewContinue &&
              !showSetupPage &&
              step !== 'ended' &&
              step !== 'conclusion' &&
              !isUploading
            ) {
              endInterviewEarly('Camera was disconnected')
            }
          })
        })
      }

      // Microphone track end listener
      if (micStreamRef.current) {
        const audioTracks = micStreamRef.current.getAudioTracks()
        audioTracks.forEach((track) => {
          track.addEventListener('ended', () => {
            console.log('Microphone track ended event fired')
            if (
              canInterviewContinue &&
              !showSetupPage &&
              step !== 'ended' &&
              step !== 'conclusion' &&
              !isUploading
            ) {
              endInterviewEarly('Microphone was disconnected')
            }
          })
        })
      }

      // Screen sharing track end listener
      if (screenStreamRef.current) {
        const videoTracks = screenStreamRef.current.getVideoTracks()
        videoTracks.forEach((track) => {
          track.addEventListener('ended', () => {
            console.log('Screen sharing track ended event fired')
            if (
              canInterviewContinue &&
              !showSetupPage &&
              step !== 'ended' &&
              step !== 'conclusion' &&
              !isUploading
            ) {
              endInterviewEarly('Screen sharing was stopped')
            }
          })
        })
      }
    }

    // Add the event listeners with a small delay to ensure streams are ready
    setTimeout(() => {
      addTrackEndListeners()
    }, 1000)

    mediaStreamMonitorRef.current = setInterval(() => {
      // Only monitor during active interview (not during setup, conclusion, or upload)
      if (
        !canInterviewContinue ||
        showSetupPage ||
        step === 'ended' ||
        step === 'conclusion' ||
        isUploading
      ) {
        return
      }

      let shouldEndInterview = false
      let reason = ''

      // Check camera stream - more thorough check
      if (cameraStreamRef.current) {
        const videoTracks = cameraStreamRef.current.getVideoTracks()
        if (videoTracks.length === 0) {
          shouldEndInterview = true
          reason = 'Camera stream was removed'
        } else {
          const track = videoTracks[0]
          if (!track.enabled) {
            shouldEndInterview = true
            reason = 'Camera was disabled'
          } else if (track.readyState === 'ended') {
            shouldEndInterview = true
            reason = 'Camera was stopped'
          }
        }
      } else {
        shouldEndInterview = true
        reason = 'Camera stream is missing'
      }

      // Check microphone stream - more thorough check
      if (micStreamRef.current && !shouldEndInterview) {
        const audioTracks = micStreamRef.current.getAudioTracks()
        if (audioTracks.length === 0) {
          shouldEndInterview = true
          reason = 'Microphone stream was removed'
        } else {
          const track = audioTracks[0]
          if (!track.enabled) {
            shouldEndInterview = true
            reason = 'Microphone was disabled'
          } else if (track.readyState === 'ended') {
            shouldEndInterview = true
            reason = 'Microphone was stopped'
          }
        }
      } else if (!micStreamRef.current && !shouldEndInterview) {
        shouldEndInterview = true
        reason = 'Microphone stream is missing'
      }

      // Check screen sharing stream - more thorough check
      if (screenStreamRef.current && !shouldEndInterview) {
        const videoTracks = screenStreamRef.current.getVideoTracks()
        if (videoTracks.length === 0) {
          shouldEndInterview = true
          reason = 'Screen sharing stream was removed'
        } else {
          const track = videoTracks[0]
          if (!track.enabled) {
            shouldEndInterview = true
            reason = 'Screen sharing was disabled'
          } else if (track.readyState === 'ended') {
            shouldEndInterview = true
            reason = 'Screen sharing was stopped'
          }
        }
      } else if (!screenStreamRef.current && !shouldEndInterview) {
        shouldEndInterview = true
        reason = 'Screen sharing stream is missing'
      }

      if (shouldEndInterview) {
        console.log('Ending interview due to:', reason)
        clearInterval(mediaStreamMonitorRef.current)
        endInterviewEarly(reason)
      }
    }, 500) // Check every 500ms for more responsive detection
  }, [
    canInterviewContinue,
    showSetupPage,
    step,
    isUploading,
    endInterviewEarly,
  ])

  const finishInterview = useCallback(() => {
    setStep('conclusion')
    setSpokenTextHistory((prev) => [
      ...prev,
      { text: conclusion, type: 'conclusion' },
    ])

    speakWithOpenAI(conclusion, async () => {
      const now = new Date()
      const elapsedMs = now - startTimeRef.current
      const elapsedSec = Math.floor(elapsedMs / 1000)
      const timestamp = new Date(elapsedSec * 1000).toISOString().substr(14, 5)

      setQuestionTimestamps((prev) => [
        ...prev,
        { time: timestamp, label: 'Conclusion' },
      ])

      // Auto-exit fullscreen after conclusion is finished
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
      }

      await stopRecordingAndUpload()
    })
  }, [conclusion, speakWithOpenAI, stopRecordingAndUpload])

  const startQuestion = useCallback(
    (index) => {
      const q = questions[index]
      if (!q) return

      const now = new Date()
      const elapsedMs = now - startTimeRef.current
      const elapsedSec = Math.floor(elapsedMs / 1000)
      const timestamp = new Date(elapsedSec * 1000).toISOString().substr(14, 5) // "MM:SS"

      setQuestionTimestamps((prev) => [...prev, { time: timestamp, id: q.id }])

      setCurrentQuestionIndex(index)
      setVisibleQuestions((prev) => [...prev, q])
      setTimeLeft(q.time_limit === 0 ? null : q.time_limit || 60)
      setStep('question')
      speakWithOpenAI(q.question)
    },
    [questions, speakWithOpenAI]
  )

  const handleTimeUp = useCallback(
    (noMessage = false) => {
      const isLastQuestion = currentQuestionIndex === questions.length - 1
      let message = isLastQuestion
        ? 'Time is up.'
        : "Time is up, let's continue with the next question."

      if (noMessage) {
        // Skip speaking, just move to next
        if (isLastQuestion) {
          finishInterview()
        } else {
          startQuestion(currentQuestionIndex + 1)
        }
        return
      }

      speakWithOpenAI(message, () => {
        if (isLastQuestion) {
          finishInterview()
        } else {
          startQuestion(currentQuestionIndex + 1)
        }
      })
    },
    [
      currentQuestionIndex,
      questions.length,
      finishInterview,
      startQuestion,
      speakWithOpenAI,
    ]
  )

  // Detect Chrome browser
  useEffect(() => {
    const isChrome =
      /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)
    setIsChromeBrowser(isChrome)
  }, [])

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )

      const currentState = stateRef.current

      // If user exits fullscreen during conclusion, show processing modal
      if (
        !isCurrentlyFullscreen &&
        currentState.canInterviewContinue &&
        !currentState.showSetupPage &&
        currentState.step === 'conclusion' &&
        !currentState.isUploading
      ) {
        setIsUploading(true)
        return
      }

      // If user exits fullscreen during active interview, start countdown
      if (
        !isCurrentlyFullscreen &&
        currentState.canInterviewContinue &&
        !currentState.showSetupPage &&
        currentState.step !== 'ended' &&
        currentState.step !== 'conclusion' &&
        !currentState.showExitWarning &&
        !currentState.isUploading
      ) {
        // Clear any existing timer first
        if (exitTimerRef.current) {
          clearInterval(exitTimerRef.current)
          exitTimerRef.current = null
        }

        setShowExitWarning(true)
        setExitCountdown(10) // Always start from 10

        exitTimerRef.current = setInterval(() => {
          setExitCountdown((prev) => {
            const newCount = prev - 1
            if (newCount <= 0) {
              clearInterval(exitTimerRef.current)
              exitTimerRef.current = null
              resetToSetupPage(true) // true = show exited screen
              return 0
            }
            return newCount
          })
        }, 1000)
      } else if (isCurrentlyFullscreen && currentState.showExitWarning) {
        // User returned to fullscreen, cancel warning
        setShowExitWarning(false)
        if (exitTimerRef.current) {
          clearInterval(exitTimerRef.current)
          exitTimerRef.current = null
        }
      }
    }

    // Add event listeners for all browsers
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      )
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      )
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
      if (exitTimerRef.current) {
        clearInterval(exitTimerRef.current)
        exitTimerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally empty to prevent re-running during step changes

  const requestMediaPermissions = useCallback(async () => {
    try {
      setPermissionError('')
      setIsSetupLoading(true)

      // First, get camera and microphone
      let cameraStream, micStream, screenStream

      try {
        cameraStream = await navigator.mediaDevices.getUserMedia({
          video: true,
        })
        cameraStreamRef.current = cameraStream
      } catch (err) {
        setPermissionError(
          'Camera access denied. Please allow camera access and try again.'
        )
        setIsSetupLoading(false)
        return null
      }

      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        micStreamRef.current = micStream
      } catch (err) {
        setPermissionError(
          'Microphone access denied. Please allow microphone access and try again.'
        )
        cameraStream?.getTracks().forEach((track) => track.stop())
        setIsSetupLoading(false)
        return null
      }

      // Request screen sharing (with different approach for different browsers)
      try {
        if (isChromeBrowser) {
          // Chrome: Request screen with system audio
          screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: {
              displaySurface: 'monitor',
              logicalSurface: true,
              cursor: 'always',
            },
            audio: true,
          })

          // Check if system audio was actually granted
          if (screenStream.getAudioTracks().length === 0) {
            setPermissionError(
              'System audio not shared. Please check "Share audio" checkbox when sharing your screen for complete recording.'
            )
            // Don't return null - continue without system audio
          }
        } else {
          // Other browsers: Request screen without system audio
          screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: false, // Most browsers don't support system audio
          })
        }
        screenStreamRef.current = screenStream
      } catch (err) {
        setPermissionError(
          'Screen sharing denied. Please allow screen sharing and try again.'
        )
        cameraStream?.getTracks().forEach((track) => track.stop())
        micStream?.getTracks().forEach((track) => track.stop())
        setIsSetupLoading(false)
        return null
      }

      const videoTrack = screenStream.getVideoTracks()[0]
      const micAudioTrack = micStream.getAudioTracks()[0]
      const cameraVideoTrack = cameraStream.getVideoTracks()[0]

      if (!videoTrack || !micAudioTrack || !cameraVideoTrack) {
        setPermissionError(
          'Failed to access required media devices. Please ensure all permissions are granted.'
        )
        screenStream?.getTracks().forEach((t) => t.stop())
        micStream?.getTracks().forEach((t) => t.stop())
        cameraStream?.getTracks().forEach((t) => t.stop())
        setIsSetupLoading(false)
        return null
      }

      // Combine streams based on browser capabilities
      let finalStream
      if (isChromeBrowser && screenStream.getAudioTracks().length > 0) {
        // Chrome: Mix system audio with microphone
        const audioContext = new AudioContext()
        const destination = audioContext.createMediaStreamDestination()

        const systemSource = audioContext.createMediaStreamSource(
          new MediaStream([screenStream.getAudioTracks()[0]])
        )
        const micSource = audioContext.createMediaStreamSource(
          new MediaStream([micAudioTrack])
        )

        systemSource.connect(destination)
        micSource.connect(destination)

        finalStream = new MediaStream([
          videoTrack, // Only use screen sharing video for recording
          ...destination.stream.getAudioTracks(),
        ])
      } else {
        // Other browsers: Use only microphone audio and screen sharing video
        finalStream = new MediaStream([
          videoTrack, // Only use screen sharing video for recording
          micAudioTrack,
        ])
      }

      setIsSetupLoading(false)
      return finalStream
    } catch (err) {
      console.error('Media capture failed:', err)
      setPermissionError(
        `Failed to access media devices: ${err.message}. Please check your permissions and try again.`
      )
      setIsSetupLoading(false)
      return null
    }
  }, [isChromeBrowser])

  // Modified startRecording to stop if canInterviewStart is false

  const startRecording = useCallback(async (mediaStream) => {
    try {
      // Use compatible codec based on browser with better Firefox support
      let mimeType = 'video/webm'

      // Check for Firefox-compatible codecs first
      if (MediaRecorder.isTypeSupported('video/webm; codecs=vp8,opus')) {
        mimeType = 'video/webm; codecs=vp8,opus'
      } else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9,opus')) {
        mimeType = 'video/webm; codecs=vp9,opus'
      } else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9')) {
        mimeType = 'video/webm; codecs=vp9'
      } else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp8')) {
        mimeType = 'video/webm; codecs=vp8'
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        mimeType = 'video/webm'
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        mimeType = 'video/mp4'
      }

      console.log('Using MIME type:', mimeType)
      mediaRecorderRef.current = new MediaRecorder(mediaStream, { mimeType })
      recordedChunksRef.current = []

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data)
        }
      }

      mediaRecorderRef.current.start()
      startTimeRef.current = new Date()
      return true
    } catch (err) {
      setPermissionError(
        `Failed to start recording: ${err.message}. Please try again.`
      )
      return false
    }
  }, [])

  const handleFullscreen = () => {
    const elem = containerRef.current
    if (!elem) return

    if (elem.requestFullscreen) {
      elem.requestFullscreen()
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen()
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen()
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen()
    }
  }

  const handleExitInterview = () => {
    if (
      window.confirm(
        'Are you sure you want to exit the interview? This will end your interview session.'
      )
    ) {
      AxiosInstance.put(`/recruitment/interviews/${params.interview_id}`, {
        exited_interview: 1,
      }).then(() => {})
      resetToSetupPage(true) // true = show exited screen
    }
  }

  const handleReturnToFullscreen = () => {
    setShowExitWarning(false)
    if (exitTimerRef.current) {
      clearInterval(exitTimerRef.current)
      exitTimerRef.current = null
    }
    handleFullscreen()
  }

  const handleStartInterview = async () => {
    // Request all media permissions
    const mediaStream = await requestMediaPermissions()
    if (!mediaStream) {
      return
    }

    // Start recording
    const recordingStarted = await startRecording(mediaStream)
    if (!recordingStarted) {
      return
    }

    // Hide setup page and show interview
    setShowSetupPage(false)
    setCanInterviewContinue(true)

    // Start monitoring media streams for interruptions
    startMediaStreamMonitoring()

    // Enter fullscreen AFTER everything is set up
    setTimeout(() => {
      handleFullscreen()
    }, 500)
  }

  const handleCancelInterview = () => {
    history.push('/')
  }

  const handleSuccessFinish = () => {
    history.push('/')
  }

  const startListening = () => {
    if (!SpeechRecognition) return

    const recognition = new SpeechRecognition()
    recognitionRef.current = recognition
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          setQuestions((prev) =>
            prev.map((q, idx) =>
              idx === currentQuestionIndex
                ? { ...q, answer: (q.answer || '') + ' ' + transcript }
                : q
            )
          )
        }
      }
    }

    recognition.onerror = (e) => {}

    recognition.start()
  }

  const stopListening = () => {
    recognitionRef.current?.stop()
  }

  const handleMicToggle = (enabled) => {
    if (enabled) {
      micWasOnRef.current = true
      startListening()

      if (
        step === 'question' &&
        timeLeft === 0 &&
        questions[currentQuestionIndex]?.time_limit > 0
      ) {
        setTimeLeft(questions[currentQuestionIndex]?.time_limit || 60)
      }

      // setIsAnswering(true)
    } else {
      stopListening()
      // setIsAnswering(false)

      // Handle advancing to next question when mic is turned off during a question
      if (step === 'question' && micWasOnRef.current) {
        clearInterval(timerRef.current)

        // For questions with time limits
        if (timeLeft > 0 && questions[currentQuestionIndex]?.time_limit > 0) {
          handleTimeUp(true)
        }
        // For questions without time limits (time_limit === 0 or null)
        else if (
          questions[currentQuestionIndex]?.time_limit === 0 ||
          timeLeft === null
        ) {
          handleTimeUp(true)
        }
      }

      // Reset micWasOnRef so that it doesn't get reused incorrectly
      micWasOnRef.current = false
    }
  }

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  useEffect(() => {
    if (
      !location.search.includes('application_id') ||
      !location.search.includes('email') ||
      !location.search.includes('interview_id')
    ) {
      history.push('/')
    }
  }, [location.search, history])

  useEffect(() => {
    if (params.application_id) {
      dispatch(getApplication(params.application_id))
    }
  }, [dispatch, params.application_id])

  useEffect(() => {
    if (
      jobApplication &&
      jobApplication.took_interview &&
      jobApplication.successfully_finished &&
      !jobApplication.exited_interview
    ) {
      setShowSuccessScreen(true)
    }
  }, [jobApplication])

  useEffect(() => {
    if (
      jobApplication &&
      jobApplication.took_interview &&
      (jobApplication.exited_interview || !jobApplication.successfully_finished)
    ) {
      setShowExitedScreen(true)
    }
  }, [jobApplication])

  // Validate interview access permissions
  // useEffect(() => {
  //   // Wait for all required data to be available
  //   if (
  //     !jobApplication ||
  //     !jobApplication.id ||
  //     !jobApplication.email ||
  //     !params.application_id ||
  //     !params.email ||
  //     !params.interview_id ||
  //     validationChecked
  //   ) {
  //     return
  //   }

  //   const today = new Date()
  //   const interviewDate = new Date(jobApplication.interview_date)

  //   // Set both dates to start of day for accurate comparison
  //   const todayStart = new Date(
  //     today.getFullYear(),
  //     today.getMonth(),
  //     today.getDate()
  //   )
  //   const interviewDateStart = new Date(
  //     interviewDate.getFullYear(),
  //     interviewDate.getMonth(),
  //     interviewDate.getDate()
  //   )

  //   // Check all validation conditions
  //   const isAiInterviewDisabled = jobApplication.with_ai === 0
  //   const isEmailMismatch = params.email !== jobApplication.email
  //   const isWrongDate = todayStart.getTime() !== interviewDateStart.getTime()
  //   const isWrongInterviewId =
  //     parseInt(params.interview_id) !== jobApplication.interview_id
  //   const otherUser = user ? user.email !== jobApplication.email : false
  //   // Mark validation as checked to prevent repeated runs
  //   setValidationChecked(true)
  //   console.log(
  //     isAiInterviewDisabled,
  //     isEmailMismatch,
  //     isWrongDate,
  //     isWrongInterviewId,
  //     otherUser,
  //     jobApplication,
  //     'leart1033'
  //   )
  //   // true false true true false 'leart1033'
  //   if (
  //     isAiInterviewDisabled ||
  //     isEmailMismatch ||
  //     isWrongDate ||
  //     isWrongInterviewId ||
  //     otherUser
  //   ) {
  //     history.push('/')
  //     return
  //   }
  // }, [jobApplication, params, history, validationChecked, user])

  // Cleanup all media streams, recorders, and recognition on unmount/navigation
  useEffect(() => {
    return () => {
      // Stop media stream monitoring
      if (mediaStreamMonitorRef.current) {
        clearInterval(mediaStreamMonitorRef.current)
      }
      // Stop MediaRecorder
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        try {
          mediaRecorderRef.current.stop()
        } catch (e) {}
      }
      // Stop speech recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop()
        } catch (e) {}
      }
      // Stop any audio playback
      if (audioRef.current) {
        try {
          audioRef.current.pause()
          audioRef.current.currentTime = 0
        } catch (e) {}
      }
      // Stop all tracks from streams
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((track) => track.stop())
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop())
      }
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [])

  // Fetch interview data when interview starts
  useEffect(() => {
    if (!canInterviewContinue) return

    AxiosInstance.put(`/recruitment/interviews/${params.interview_id}`, {
      took_interview: 1,
    }).then(() => {})

    const fetchInterviewData = async () => {
      try {
        const res = await AxiosInstance.post(
          '/recruitment/ai-interview/start',
          { application_id: params?.application_id }
        )
        setJobTitle(res.title || '')
        setQuestions((res.questions || []).map((q) => ({ ...q, answer: '' })))
        setConclusion(res.conclusion || '')
        setEndOnTabChange(res.end_on_tab_change || false)

        if (res.introduction) {
          setSpokenTextHistory([{ text: res.introduction, type: 'intro' }])
          speakWithOpenAI(res.introduction, () => {
            setStep('wait')
          })
        }
      } catch (err) {
        setPermissionError('Failed to load interview data. Please try again.')
      }
    }

    fetchInterviewData()

    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
      clearInterval(timerRef.current)
    }
  }, [
    canInterviewContinue,
    params?.application_id,
    params?.interview_id,
    speakWithOpenAI,
  ])

  useEffect(() => {
    if (step === 'wait' && questions.length > 0) {
      const timer = setTimeout(() => {
        startQuestion(0)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [step, questions, startQuestion])

  useEffect(() => {
    if (step !== 'question' || timeLeft === null || isSpeaking) return

    // Only start timer if question has a time limit (timeLeft > 0)
    if (timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current)
            handleTimeUp()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => clearInterval(timerRef.current)
  }, [step, timeLeft, isSpeaking, handleTimeUp])

  // Handle interview end based on configuration
  useEffect(() => {
    if (endOnTabChange && canInterviewContinue) {
      // Could add tab change detection here if needed
    }
  }, [endOnTabChange, canInterviewContinue])

  const handleTextAnswer = (answer) => {
    setQuestions((prev) => {
      const updated = prev.map((q, idx) =>
        idx === currentQuestionIndex ? { ...q, answer } : q
      )
      return updated
    })
    clearInterval(timerRef.current)
    handleTimeUp(true)
  }

  // Show exited screen if interview was not completed successfully
  if (showExitedScreen) {
    return (
      <ExitedModal
        onFinish={handleSuccessFinish}
        theme={theme}
        logoLight={logoLight}
        jobTitle={jobTitle}
        interviewId={params.interview_id}
        hasAppeal={jobApplication.appeal}
        applicant={jobApplication.name}
      />
    )
  }

  // Show success screen after interview completion
  if (showSuccessScreen) {
    return (
      <SuccessModal
        onFinish={handleSuccessFinish}
        theme={theme}
        logoLight={logoLight}
        jobTitle={jobTitle}
      />
    )
  }

  // Show setup page if not started yet
  if (showSetupPage) {
    return (
      <WarningModal
        onConfirm={handleStartInterview}
        onCancel={handleCancelInterview}
        permissionError={permissionError}
        isLoading={isSetupLoading}
        theme={theme}
        logoLight={logoLight}
        jobTitle={jobTitle}
      />
    )
  }

  // Show interview interface
  return (
    <div ref={containerRef} className='ai-interview-container'>
      <CameraPreview />
      <div className='d-flex align-items-center justify-content-between w-100 position-fixed interview-header'>
        <div className='d-flex align-items-center'>
          <img
            role='button'
            onClick={() => {
              if (
                window.confirm('Are you sure you want to return to setup page?')
              ) {
                resetToSetupPage()
              }
            }}
            className='light-image ml-4'
            style={{ height: '50px' }}
            src={
              theme.loginLogo && theme.loginLogo.url
                ? theme.loginLogo.url
                : logoLight
            }
            alt=''
          />
          <div className='job-title-header'>
            <h1 className='text-white m-0'>AI Interview - {jobTitle}</h1>
          </div>
        </div>

        {step !== 'conclusion' && (
          <Button
            color='danger'
            size='sm'
            className='mr-4'
            onClick={handleExitInterview}
            style={{ zIndex: 9999 }}
          >
            <FontAwesomeIcon icon={faTimes} className='mr-1' />
            Exit Interview
          </Button>
        )}
      </div>

      {/* Fullscreen Exit Warning Modal */}
      <Modal
        isOpen={showExitWarning}
        centered
        backdrop={false}
        keyboard={false}
        style={{ zIndex: 10001 }}
        contentClassName='exit-warning-modal-content'
      >
        <ModalBody className='text-center p-4'>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            size='3x'
            className='text-warning mb-3'
          />
          <h4 className='text-danger'>⚠️ Fullscreen Mode Exited</h4>
          <p className='mb-3'>
            You have exited fullscreen mode. The interview will automatically
            end in:
          </p>
          <div className='display-4 text-danger font-weight-bold mb-3'>
            {exitCountdown}
          </div>
          <p className='text-muted'>
            Return to fullscreen mode to continue the interview.
          </p>
          <div
            className='d-flex justify-content-center'
            style={{ gap: '1rem' }}
          >
            <Button
              color='success'
              onClick={handleReturnToFullscreen}
              style={{ zIndex: 10002 }}
            >
              Return to Fullscreen
            </Button>
            <Button
              color='danger'
              onClick={() => {
                AxiosInstance.put(
                  `/recruitment/interviews/${params.interview_id}`,
                  {
                    exited_interview: 1,
                  }
                ).then(() => {})
                resetToSetupPage(true) // true = show exited screen
              }}
              style={{ zIndex: 10002 }}
            >
              End Interview
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Upload Loading Modal */}
      <Modal
        isOpen={isUploading}
        centered
        backdrop='static'
        keyboard={false}
        style={{ zIndex: 10000 }}
        contentClassName='upload-modal-content'
      >
        <ModalBody className='text-center p-4'>
          <div className='spinner-border text-primary mb-3' role='status'>
            <span className='sr-only'>Loading...</span>
          </div>
          <h4 className='text-primary'>Processing Interview...</h4>
          <p className='mb-3'>
            Please wait while we save your interview recording. This may take a
            few moments.
          </p>
          <p className='text-danger font-weight-bold'>
            ⚠️ Please do not close this page or navigate away until the process
            is complete.
          </p>
        </ModalBody>
      </Modal>

      <div className='intro-text px-4 py-2 w-50 mx-auto mb-4 scrollbar chat-body'>
        {/* Intro */}
        {spokenTextHistory
          .filter((el) => el.type === 'intro')
          .map((line, index) => (
            <>
              <p className='my-3' key={index}>
                {line.text}
              </p>
            </>
          ))}

        {/* Questions */}
        {visibleQuestions.map((q, idx) => {
          const fullQuestion = questions.find(
            (qq) => qq.question === q.question
          )

          return (
            <div
              key={q.id || idx}
              className='mt-4 p-3 border border-primary rounded bg-light'
            >
              <h5 className='mb-2'>Question {idx + 1}:</h5>
              <p className='font-weight-bold'>{q.question}</p>

              {currentQuestionIndex === idx &&
                timeLeft !== null &&
                timeLeft > 0 && (
                  <p className='text-danger'>
                    Time Remaining: <strong>{formatTime(timeLeft)}</strong>
                  </p>
                )}

              {currentQuestionIndex === idx &&
                questions[currentQuestionIndex]?.time_limit === 0 && (
                  <p className='text-info'>
                    <strong>No time limit - Take your time to answer</strong>
                  </p>
                )}

              {fullQuestion?.answer && (
                <div className='mt-2'>
                  <strong className='text-muted'>Answer:</strong>
                  <p className='mb-0'>{fullQuestion.answer}</p>
                </div>
              )}
            </div>
          )
        })}

        {/* Conclusion */}
        {spokenTextHistory
          .filter((el) => el.type === 'conclusion')
          .map((line, index) => (
            <>
              <p className='my-3' key={index}>
                {line.text}
              </p>
            </>
          ))}
      </div>

      <Footer
        isSpeaking={isSpeaking}
        onMicToggle={handleMicToggle}
        allowInput={questions?.[currentQuestionIndex]?.allow_input}
        onTextAnswer={handleTextAnswer}
        canInterviewContinue={canInterviewContinue}
        step={step}
      />
    </div>
  )
}

export default AIInterviewTest
